/**
 * Common Insert Parameters Utility
 * Provides standardized parameters for API insert operations
 *
 * Migrated from: 3in1cms/bajajfinserv-web/ui.solid/src/lib/sso/util.ts
 * Adapted for Redux-based state management (instead of SolidJS signals)
 */

import { getUtmParamsFromCookie } from "./common-utils";
import { isOpenInApp } from "./pwa-utils";

/**
 * Helper to provide default value for undefined/null fields
 * @param value - Value to check
 * @param defaultValue - Default value to return if value is undefined/null (defaults to empty string)
 * @returns The value or default
 */
export function getOrDefaultValue(
  value: string | undefined | null,
  defaultValue: string = ""
): string {
  return value ?? defaultValue;
}

/**
 * Common insert parameters overrides interface
 */
export interface ICommonInsertOverrides {
  id?: string;
  appVersion?: string;
  saveForLaterFlag?: boolean;
  pwaVersion?: string;
  cta?: string;
  appDtls?: {
    custId?: string;
    custType?: string;
    profession?: string;
    subProfession?: string;
  };
}

/**
 * Generate common insert parameters for API requests
 *
 * @param overrides - Optional parameter overrides
 * @returns Object with standard insert parameters (UTM tracking, app details, etc.)
 *
 * @example
 * ```typescript
 * const params = commonInsertParameters({
 *   appVersion: "v4",
 *   appDtls: { custId: "12345" }
 * });
 *
 * // Use in API request
 * await request({
 *   operationId: OPERATION_IDS.hlInsert,
 *   requestData: {
 *     ...commonInsertParameters(),
 *     eventCode: HL_INSERT_EVENT_CODE,
 *     ...customData
 *   }
 * });
 * ```
 */
export const commonInsertParameters = (overrides?: ICommonInsertOverrides) => {
  const utmParams = getUtmParamsFromCookie();

  return {
    id: overrides?.id ?? "",
    sourceSystem: "3in1",
    srcChannel: isOpenInApp() ? "3in1 App" : "3in1 Web",
    appVersion: overrides?.appVersion ?? "v3",
    saveForLaterFlag: overrides?.saveForLaterFlag ?? false,
    sourceUrl: typeof window !== "undefined" ? window.location.href : "",
    pwaVersion: overrides?.pwaVersion ?? "",
    cta: overrides?.cta ?? "Continue",
    appDtls: [
      {
        custId: overrides?.appDtls?.custId ?? "",
        ...(overrides?.appDtls?.custType && { custType: overrides.appDtls.custType }),
        profession: overrides?.appDtls?.profession ?? "",
        subProfession: overrides?.appDtls?.subProfession ?? "",
        appType: "Primary",
      },
    ],
    utmSource: getOrDefaultValue(utmParams?.utmSource as string),
    utmContent: getOrDefaultValue(utmParams?.utmContent as string),
    utmCampaign: getOrDefaultValue(utmParams?.utmCampaign as string),
    utmMedium: getOrDefaultValue(utmParams?.utmMedium as string),
    utmTerm: "",
  };
};

/**
 * Get login state from Redux store
 * Note: This is a placeholder - actual implementation should use your Redux store
 *
 * @returns Boolean indicating if user is logged in
 */
export const getLoginState = (): boolean => {
  // TODO: Replace with actual Redux selector
  // Example: return useSelector((state: RootState) => state.auth.isLoggedIn)

  // For now, check localStorage for access token as fallback
  if (typeof window === "undefined") return false;

  const accessToken = localStorage.getItem("access_token");
  return !!accessToken;
};
